import { defineStore } from 'pinia'
import { watch } from 'vue'
import { useMacroStore } from './macro'
import { useDeviceStore } from './device'
import winKeyCodeJson from '../utils/keyboard.json'
import macKeyCodeJson from '../utils/macKeyboard.json'
import { deepClone } from '../utils/index.js'
import { commonChars } from '@/assets/json/keybindingData'
import { loadKeyboard, getSupportedLanguages, getSupportedLayouts } from '../utils/keyboardLoader.js'
import { getDeviceConfig } from '@/config/keyboardDevices'

const KEY_BINDING_COMMAND_DELAY_MS = 100
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const getMacroStoreBindTypeByUsageId = (macroStore, usageId) => {
    const normalizedUsageId = Number(usageId)
    if (!Number.isInteger(normalizedUsageId) || normalizedUsageId < 0 || normalizedUsageId > 0xff) return null

    const macros = Array.isArray(macroStore.macros) ? macroStore.macros : []
    const hasQuickLaunchBind = macros.some((macro) =>
        macro?.bindingMode === 'quickLaunch' &&
        Array.isArray(macro?.quickLaunch?.uidList) &&
        macro.quickLaunch.uidList.some(uid => Number(uid) === normalizedUsageId)
    )
    if (hasQuickLaunchBind) return 'quickLaunch'

    const hasMacroBind = macros.some((macro) =>
        macro?.bindingMode === 'macro' &&
        Array.isArray(macro?.bindKeys) &&
        macro.bindKeys.some(uid => Number(uid) === normalizedUsageId)
    )
    return hasMacroBind ? 'macro' : null
}

export const useKeyboardStore = defineStore('keyboard', {
    state: () => ({
        // 当前使用的键盘配置 - 实际的状态数据
        keyCodeJson: [],
        // 默认键盘配置的备份
        defaultKeyCodeJson: [],
        // 记录被修改过的键位索引
        modifiedKeyIndices: new Set(),
        // 记录原始键名
        originalKeyNames: {},
        keyboardIndex: null, // 当前点击的键盘按键索引
        // 当前语言设置 - 从 localStorage 读取，如果没有则默认为 'default'
        currentLanguage: localStorage.getItem('keyboardLanguage') || 'default',
        // 支持的语言列表
        supportedLanguages: getSupportedLanguages(),
        // 当前布局设置 ('standard' 或 'eu')
        currentLayout: 'standard',
        // 支持的布局列表
        supportedLayouts: getSupportedLayouts(),
        currentFnPage: 1, // 当前功能键分页（支持动态页数）
        totalFnPages: 6, // 总页数（可动态增减）
        currentThemeStyle: [],
        // 保存每个分页的原始按键数据（完整的六个按键配置）
        fnPageKeysBackup: {
            1: {}, // 第1页的六个按键数据备份
            2: {}, // 第2页的六个按键数据备份
            3: {},  // 第3页的六个按键数据备份
            7: {}
        },
        // 第4-6页的独立数据存储（直接来自keyboard.json的分页数据）
        fnPagesDataStore: {
            4: {}, // 第4页的独立数据
            5: {}, // 第5页的独立数据
            6: {}  // 第6页的独立数据
        },
        // 每个分页的按键映射（定义哪些按键属于功能键区域）
        fnKeyCodes: ['Insert', 'Home', 'PageUp', 'Delete', 'End', 'PageDown'],
        fnKeyMapping: {
            1: {// 第1页
                'Insert': { img: 'theme1-0', label: 'Ins' },
                'Home': { img: 'theme1-1', label: 'Home' },
                'PageUp': { img: 'theme1-2', label: 'Pgup' },
                'Delete': { img: 'theme1-3', label: 'Del' },
                'End': { img: 'theme1-4', label: 'End' },
                'PageDown': { img: 'theme1-5', label: 'Pgdn' }
            },
            2: { // 第2页
                'Insert': { img: 'fn2-ins', label: 'Fn2-Ins' },
                'Home': { img: 'fn2-home', label: 'Fn2-Home' },
                'PageUp': { img: 'fn2-pgup', label: 'Fn2-Pgup' },
                'Delete': { img: 'fn2-del', label: 'Fn2-Del' },
                'End': { img: 'fn2-end', label: 'Fn2-End' },
                'PageDown': { img: 'fn2-pgdn', label: 'Fn2-Pgdn' }
            },
            3: {
                'Insert': { img: 'fn3-ins', label: 'Fn3-Ins' },
                'Home': { img: 'fn3-home', label: 'Fn3-Home' },
                'PageUp': { img: 'fn3-pgup', label: 'Fn3-Pgup' },
                'Delete': { img: 'fn3-del', label: 'Fn3-Del' },
                'End': { img: 'fn3-end', label: 'Fn3-End' },
                'PageDown': { img: 'fn3-pgdn', label: 'Fn3-Pgdn' }
            },
            7: {
                'Insert': { img: 'w1', label: 'Fn3-Ins' },
                'Home': { img: 'w2', label: 'Fn3-Home' },
                'PageUp': { img: 'w3', label: 'Fn3-Pgup' },
                'Delete': { img: 'w4', label: 'Fn3-Del' },
                'End': { img: 'w5', label: 'Fn3-End' },
                'PageDown': { img: 'w6', label: 'Fn3-Pgdn' }
            }
        }
    }),
    getters: {
        // 根据键盘类型返回对应的原始keyCodeJson
        getCurrentKeyCodeJson: (state) => {
            const deviceStore = useDeviceStore()
            const keyboardType =
                deviceStore.lightData.keyboardType

            if (keyboardType === 'M') {
                return macKeyCodeJson
            } else {
                return winKeyCodeJson
            }

        },

    },
    actions: {
        // 切换分页
        setFnPage(page) {
            // 验证页数有效性（支持动态页数）
            if (page >= 1 && page <= this.totalFnPages && page !== this.currentFnPage) {
                // 1. 保存当前分页的六个按键完整数据
                this.saveFnPageKeys(this.currentFnPage)

                // 2. 切换到新分页
                this.currentFnPage = page

                // 3. 恢复新分页的按键数据
                this.restoreFnPageKeys(page)

                console.log(`[Fn分页] 已切换到第${page}页`)
            }
        },

        // 保存当前分页的六个按键完整数据
        saveFnPageKeys(page) {
            // 页面 4-6：保存到独立数据存储
            // if (page >= 4 && page <= 6) {
            //     const pageData = {}
            //     this.keyCodeJson.forEach(key => {
            //         if (key.fnSlot >= 1 && key.fnSlot <= 6 && key.page == page) {
            //             pageData[key.fnSlot] = JSON.parse(JSON.stringify(key))
            //         }
            //     })
            //     this.fnPagesDataStore[page] = pageData
            //     console.log(`[Fn分页] 已保存第${page}页的独立数据 (按 fnSlot 标识)`, pageData)
            //     return
            // }

            // 页面 1-3：保存到备份机制（原有逻辑）
            const backup = {}
            this.keyCodeJson.forEach(key => {
                // 使用 fnSlot (1-6) 作为标识符，而不是 code
                if (key.fnSlot >= 1 && key.fnSlot <= 6 && !key.page) {
                    backup[key.fnSlot] = JSON.parse(JSON.stringify(key))
                }
            })
            this.fnPageKeysBackup[page] = backup
            console.log(`[Fn分页] 已保存第${page}页的按键数据 (按 fnSlot 标识)`, backup)
        },

        // 恢复目标分页的按键数据
        restoreFnPageKeys(page, type = '') {
            const deviceStore = useDeviceStore()
            const isMac = deviceStore.lightData.keyboardType === 'M'

            // 页面 4-6：使用独立数据结构
            if (page >= 4 && page <= 6) {
                const pageData = this.fnPagesDataStore[page]
                const hasPageData = Object.keys(pageData).length > 0

                this.keyCodeJson.forEach(key => {
                    if (key.fnSlot >= 0 && key.fnSlot <= 5) {
                        if (type === 'restore' && key.page) {
                            localStorage.removeItem(`fnPage${key.page}CustomImage_${key.fnSlot}`)
                            key.customImg = ''
                        }
                        if (key.page == page && type !== 'restore') {
                            // 页面数据为空，使用默认配置
                            // console.log('dataUrl', localStorage.getItem(`fnPage${page}CustomImage_${key.fnSlot}`))
                            const dataUrl = localStorage.getItem(`fnPage${page}CustomImage_${key.fnSlot}`)
                            // key.name = `fn${page}K${key.fnSlot}`
                            key.customImg = dataUrl || ''
                        }

                    }
                })
                console.log(`[Fn分页] 已恢复第${page}页的独立数据 (按 fnSlot 匹配)`)
                return
            }

            // 页面 1-3：使用备份机制（原有逻辑）
            if (!this.fnPageKeysBackup[page]) {
                this.fnPageKeysBackup[page] = {}
            }
            const backup = this.fnPageKeysBackup[page]
            const hasBackup = Object.keys(backup).length > 0

            this.keyCodeJson.forEach(key => {
                // 使用 fnSlot 来匹配备份数据
                if (key.fnSlot >= 1 && key.fnSlot <= 6 && !key.page) {
                    if (hasBackup && backup[key.fnSlot]) {
                        if (type === 'restore') {
                            const backupItem = backup[key.fnSlot]
                            key.img = backupItem.img
                            key.displayName = backupItem.displayName
                            key.name = backupItem.name
                            key.customImg = ''
                            key.isCustomImage = false
                        } else {
                            Object.assign(key, backup[key.fnSlot])
                        }
                    } else {
                        // 如果没有备份，使用 fnKeyMapping 的默认配置
                        const defaultConfig = this.fnKeyMapping[page]
                            ? this.fnKeyMapping[page][key.code]
                            : null

                        if (defaultConfig) {
                            key.img = defaultConfig.img
                            key.displayName = defaultConfig.label
                            key.customImg = ''
                            key.isCustomImage = false
                            if (page > 1) {
                                key.name = ''
                            } else {
                                key.name = defaultConfig.label
                            }
                        }
                    }

                    // 第2页的 PageDown 按键，根据键盘类型动态调整图片
                    if (page === 2 && key.code === 'PageDown') {
                        // 根据键盘类型动态添加 -mac 后缀
                        key.img = isMac ? 'fn2-pgdn-mac' : 'fn2-pgdn'
                    }
                }
            })
            console.log(`[Fn分页] 已恢复第${page}页的按键数据 (按 fnSlot 匹配)`)
        },
        // 更新自定义图片
        updateCustomImg(page, slot, dataUrl) {
            this.keyCodeJson.forEach(key => {
                // 使用 fnSlot (1-6) 作为标识符，而不是 code 设备发过来的page从3开始
                if (key.fnSlot === slot && key.page === (page + 1)) {
                    key.customImg = dataUrl
                }
            })
        },
        // 更新功能键显示（已弃用，保留兼容性）
        updateFnKeyDisplay() {
            console.warn('[Fn分页] updateFnKeyDisplay 已弃用，请使用 setFnPage')
        },

        // 添加新的分页
        addFnPage() {
            this.totalFnPages += 1
            // 初始化新页面的备份为空
            this.fnPageKeysBackup[this.totalFnPages] = {}
            console.log(`[Fn分页] 已添加新页面，总页数现为 ${this.totalFnPages}`)
        },

        // 删除指定分页（不能删除第1页）
        removeFnPage(page) {
            if (page <= 1) {
                console.warn('[Fn分页] 不能删除第1页')
                return false
            }
            if (page > this.totalFnPages) {
                console.warn(`[Fn分页] 页面 ${page} 不存在`)
                return false
            }

            // 删除该页的备份数据
            delete this.fnPageKeysBackup[page]
            this.totalFnPages -= 1

            // 如果删除的是当前页，切换到前一页
            if (this.currentFnPage === page) {
                this.setFnPage(Math.min(page - 1, this.totalFnPages))
            }

            console.log(`[Fn分页] 已删除第${page}页，总页数现为 ${this.totalFnPages}`)
            return true
        },

        // 获取总页数
        getTotalFnPages() {
            return this.totalFnPages
        },
        syncFnPagesByTransmitMode() {
            const deviceStore = useDeviceStore()
            const config = getDeviceConfig(
                Number(deviceStore.deviceVendorId),
                Number(deviceStore.deviceProductId)
            )
            const version = String(config?.version || '').trim().toLowerCase()
            const isK1W = version === 'k1-w'
            this.totalFnPages = isK1W ? 7 : 6

            if (!isK1W && this.currentFnPage === 7) {
                this.setFnPage(6)
            }
        },
        // 初始化并监听键盘类型变化
        initKeyboard() {
            const deviceStore = useDeviceStore()

            // 立即设置初始值
            this.updateKeyboardData('立即设置初始值')
            this.syncFnPagesByTransmitMode()

            // 监听 keyboardType 变化
            watch(
                () => deviceStore.lightData.keyboardType,
                () => this.updateKeyboardData('监听 keyboardType 变化'),
                { immediate: false }
            )

            watch(
                () => [deviceStore.deviceVendorId, deviceStore.deviceProductId],
                () => this.syncFnPagesByTransmitMode(),
                { immediate: true }
            )
        },
        // 更新键盘数据
        updateKeyboardData(str) {
            console.log(str)
            console.log('更新键盘类型')
            const deviceStore = useDeviceStore()
            const keyboardType = deviceStore.lightData.keyboardType

            // 备份当前分页
            const currentPage = this.currentFnPage

            // 无论在哪一页，都先保存当前页的数据（包括第1页的主题）
            this.saveFnPageKeys(currentPage)

            // 备份当前的 bindMacro 数据
            const bindMacroBackup = {}
            if (this.keyCodeJson && this.keyCodeJson.length > 0) {
                this.keyCodeJson.forEach(item => {
                    if (item.bindMacro !== undefined && item.keyCode !== undefined) {
                        bindMacroBackup[item.keyCode] = {
                            bindMacro: item.bindMacro,
                            isIcon: item.isIcon,
                            bindCode: item.bindCode,
                            bindType: item.bindType
                        }
                    }
                })
            }

            // 根据系统、布局和语言加载键盘配置
            const system = keyboardType === 'M' ? 'mac' : 'windows'
            const keyboardConfig = loadKeyboard(system, this.currentLayout, this.currentLanguage)

            this.keyCodeJson = deepClone(keyboardConfig)
            this.defaultKeyCodeJson = deepClone(keyboardConfig)

            // 恢复 bindMacro 数据
            this.keyCodeJson.forEach(item => {
                if (bindMacroBackup[item.keyCode]) {
                    item.bindMacro = bindMacroBackup[item.keyCode].bindMacro
                    item.isIcon = bindMacroBackup[item.keyCode].isIcon
                    item.bindCode = bindMacroBackup[item.keyCode].bindCode
                    item.bindType = bindMacroBackup[item.keyCode].bindType
                }
            })

            // 恢复当前页的 Fn 键数据（包括第1页的主题）
            this.restoreFnPageKeys(currentPage)

            // 清除之前的修改记录
            this.modifiedKeyIndices.clear()
            this.originalKeyNames = {}

            console.log(`[语言切换] 已恢复到第${currentPage}页的配置`)
        },
        // 把宏ID挂在 JSON数据上
        // 如果这个数据有宏ID那就去掉 没有则加上
        setKeyCodeJson(value) {
            this.keyCodeJson = deepClone(value)
        },
        // 更新单个键的配置
        updateKeyCode(index, newKeyCode) {
            if (index >= 0 && index < this.keyCodeJson.length) {
                this.keyCodeJson[index] = { ...this.keyCodeJson[index], ...newKeyCode }
            }
        },

        // 更新整个键盘配置
        updateKeyCodeJson(newKeyCodeJson) {
            this.keyCodeJson = deepClone(newKeyCodeJson)
        },

        // 从设备数据应用主题样式（设备数据驱动）
        // applyThemeFromDevice(themeStyle) {
        //     // 只在第1页时应用主题
        //     if (this.currentFnPage !== 1) {
        //         console.log(`[主题同步] 当前不在第1页，跳过`)
        //         return
        //     }
        //     // showMenu按键的keyCode与主题图片索引的映射关系
        //     const SHOW_MENU_KEY_MAPPING = {
        //         73: 0, // Ins -> theme{X}_0
        //         74: 1, // Home -> theme{X}_1
        //         75: 2, // Pgup -> theme{X}_2
        //         76: 3, // Del -> theme{X}_3
        //         77: 4, // End -> theme{X}_4
        //         78: 5  // Pgdn -> theme{X}_5
        //     }

        //     // 创建更新后的键盘配置
        //     const updatedKeyboard = this.keyCodeJson.map(key => {
        //         // 检查是否是showMenu按键
        //         if (key.class && key.class.includes('showMenu') && SHOW_MENU_KEY_MAPPING[key.keyCode] !== undefined) {
        //             const imageIndex = SHOW_MENU_KEY_MAPPING[key.keyCode]

        //             if (themeStyle >= 0 && themeStyle <= 4) {
        //                 // 选择主题1-5（themeStyle 0-4对应theme1-theme5）
        //                 return {
        //                     ...key,
        //                     img: `theme${themeStyle + 1}-${imageIndex}`
        //                 }
        //             } else if (themeStyle === 5) {
        //                 // 选择皮肤6，恢复为默认的lucency
        //                 return {
        //                     ...key,
        //                     img: 'lucency'
        //                 }
        //             }
        //         }
        //         return key
        //     })

        //     // 更新键盘配置
        //     this.keyCodeJson = deepClone(updatedKeyboard)
        //     console.log(`[主题同步] 已从设备应用主题 ${themeStyle}`)
        // },
        applyThemeFromDevice(themeStyle) {
            if (this.currentThemeStyle === themeStyle) return

            const SHOW_MENU_KEY_MAPPING = { 73: 0, 74: 1, 75: 2, 76: 3, 77: 4, 78: 5 }
            let changed = false

            this.keyCodeJson.forEach((key, i) => {
                if (!(key.class && key.class.includes('showMenu'))) return
                const imageIndex = SHOW_MENU_KEY_MAPPING[key.keyCode]
                if (imageIndex === undefined) return

                const nextImg = (themeStyle >= 0 && themeStyle <= 4)
                    ? `theme${themeStyle + 1}-${imageIndex}`
                    : 'lucency'

                if (key.img !== nextImg) {
                    this.keyCodeJson[i] = { ...key, img: nextImg } // 只改这一项
                    changed = true
                }
            })

            if (!changed) return
            this.currentThemeStyle = themeStyle
        },
        // 更新单个键位名称并记录修改
        async updateKeyName(keyData) {
            // 检查选中的this.keyCodeJson[this.keyboardIndex].keyCode
            if (this.keyCodeJson[this.keyboardIndex].keyCode >= 1000) { return }
            const deviceStore = useDeviceStore()
            const macroStore = useMacroStore()
            const { keyCode, name, code, isIcon, category } = keyData
            const isMacro = Array.isArray(category) && category.includes('macro')

            if (isMacro) {
                const macroId = String(code || name || '')
                const currentKey = this.keyCodeJson[this.keyboardIndex]
                if (currentKey.bindType === 'macro' && String(currentKey.bindMacro || '') === macroId) return

                currentKey.bindMacro = macroId
                currentKey.isIcon = !!isIcon
                currentKey.bindCode = macroId
                currentKey.bindType = 'macro'

                macroStore.setCurrentMacro(macroId)
                const targetMacro = macroStore.getCurrentMacro()
                const selectedUsageId = Number(this.keyCodeJson[this.keyboardIndex].keyCode)
                const currentBindKeys = Array.isArray(targetMacro?.bindKeys) ? targetMacro.bindKeys : []
                const nextBindKeys = [...new Set([...currentBindKeys, selectedUsageId])]
                const isQuickLaunchMode = macroStore.quickLaunchMode || targetMacro?.bindingMode === 'quickLaunch'

                if (isQuickLaunchMode) {
                    // 绑定快捷启动前，先删除该按键的宏录制绑定
                    await deviceStore.sendResetKey(selectedUsageId).catch((error) => {
                        console.warn('[keyboard] 删除宏录制绑定失败:', { usageId: selectedUsageId, error })
                    })

                    const type = String(macroStore.quickLaunchType || 'url')
                    const value = String(macroStore.quickLaunchValue || '')
                    const serial = Number.parseInt(String(macroId).replace(/^\D+/, ''), 10) || 0
                    const layer = 0

                    macroStore.setQuickLaunchForMacro(macroId, {
                        serial,
                        layer,
                        type,
                        value,
                        uidList: nextBindKeys
                    })

                    const payloadObject = { serial, usageIds: nextBindKeys, layer, type, value }
                    const payloadJson = JSON.stringify(payloadObject)
                    const payloadBytes = Array.from(new TextEncoder().encode(payloadJson))
                    setTimeout(() => {
                        deviceStore.sendQuickLaunch(payloadBytes)
                    }, 100)
                } else {
                    // 绑定宏录制前，先删除该按键的快捷启动绑定;
                    await deviceStore.sendDeleteQuickLaunch(0, selectedUsageId).catch((error) => {
                        console.warn('[keyboard] 删除快捷启动绑定失败:', { layer: 0, usageId: selectedUsageId, error })
                    })

                    macroStore.updateMacroBindKey(99, selectedUsageId);
                    // 在这里延时发送;
                    setTimeout(() => {
                        deviceStore.sendReport();
                    }, 100);
                }
            } else {
                const currentKey = this.keyCodeJson[this.keyboardIndex]
                if (Number(keyCode) === Number(currentKey.bindCode)) return

                const selectedUsageId = Number(currentKey.keyCode)
                // 先从 macroStore 查询旧绑定类型，再覆盖本地显示状态；keyCodeJson 不一定记录 quickLaunch。
                const previousBindType = getMacroStoreBindTypeByUsageId(macroStore, selectedUsageId)
                const shouldClearMacro = previousBindType === 'macro'
                const shouldClearQuickLaunch = previousBindType === 'quickLaunch'

                // 旧绑定是宏录制时，只清理宏录制绑定。
                if (shouldClearMacro) {
                    await deviceStore.sendResetKey(selectedUsageId).catch((error) => {
                        console.warn('[keyboard] 清理宏录制绑定失败:', { usageId: selectedUsageId, error })
                    })
                    await delay(KEY_BINDING_COMMAND_DELAY_MS)
                }

                // 旧绑定是快捷启动时，只清理快捷启动绑定。
                if (shouldClearQuickLaunch) {
                    await deviceStore.sendDeleteQuickLaunch(0, selectedUsageId).catch((error) => {
                        console.warn('[keyboard] 清理快捷启动绑定失败:', { layer: 0, usageId: selectedUsageId, error })
                    })
                    await delay(KEY_BINDING_COMMAND_DELAY_MS)
                }

                // 本地宏绑定数据也只在确实替换宏/快捷启动时清理。
                if (shouldClearMacro || shouldClearQuickLaunch) {
                    macroStore.removeBindKeyFromAllMacros(selectedUsageId)
                }

                // 最后覆盖为普通按键绑定，并下发普通按键绑定命令。
                currentKey.bindMacro = name
                currentKey.isIcon = !!isIcon
                currentKey.bindCode = keyCode
                currentKey.bindType = 'keyboard'

                await deviceStore.sendKeyBinding(currentKey.keyCode, keyCode)
                await delay(KEY_BINDING_COMMAND_DELAY_MS)
            }
        },
        // 更新键位名称为宏ID
        updateKeyNameToMacro(bindKeys, macroId) {
            // console.log('bindKeys-keyboard', bindKeys)
            const macroStore = useMacroStore();
            // const currentMacroId = macroStore.currentMacroId;
            const currentMacroId = macroId;

            // 先把 当前 宏Id 所有对象的 bindMacro 置为 null
            this.keyCodeJson.forEach(item => {
                if (item.bindMacro === currentMacroId) {
                    item.bindMacro = null;
                }
            });
            // currentBindKeys 有 是不是得加个
            // 根据 bindKeys 里出现的 keycode 设置对应对象的 bindMacro
            bindKeys.forEach(bk => {
                const target = this.keyCodeJson.find(k => k.keyCode === bk);
                if (target && currentMacroId) {
                    target.bindMacro = currentMacroId;
                    target.bindType = 'macro'
                    target.isIcon = false
                }
            });
        },
        // 更新键位绑定
        updateKeyReplace(keys) {
            // [{newKey: 24, oldKey: 75}]
            // 循环数组 根据 oldkey 找到对应位置 然后 修改对象中的bindMacro 值 或者说 重新创建一个值 bindKey
            // bindMacro与bindKey 不应该有冲突 它们为互斥关系 感觉很麻烦 还是统一用bindMacro吧
            // 我大脑一片空白 不知道该怎么下手 
            // 普通按键直接更换就行 但是有些按键是图标的 需要进行单独判断
            keys.forEach(key => {
                const target = this.keyCodeJson.find(k => k.keyCode === key.oldKey)
                let newTarget = this.keyCodeJson.find(k => k.keyCode === key.newKey)
                // 这里检查newTarget是否为空 空则代表是 commonChars列表里面的值
                if (!newTarget) {
                    newTarget = commonChars.find(k => k.keyCode === key.newKey)
                }
                if (target) {
                    target.bindMacro = newTarget.name
                    target.isIcon = newTarget.isIcon
                    target.bindCode = key.newKey // 添加绑定按键的 keycode值
                    target.bindType = 'keyboard'
                }
            })
        },
        // 恢复单个键位的原始名称
        restoreKeyName(index) {
            if (this.originalKeyNames[index]) {
                this.keyCodeJson[index] = {
                    ...this.keyCodeJson[index],
                    name: this.originalKeyNames[index]
                }
                this.modifiedKeyIndices.delete(index)
                delete this.originalKeyNames[index]
            }
        },
        // 重置键盘配置到默认值
        resetKeyCodeJson() {
            // 先保存当前分页的按键数据（img、name等配置）
            const currentPage = this.currentFnPage
            this.saveFnPageKeys(currentPage)
            const macroStore = useMacroStore()

            // 重置键盘配置
            this.keyCodeJson = deepClone(this.defaultKeyCodeJson)
            this.modifiedKeyIndices.clear()
            this.originalKeyNames = {}

            this.keyCodeJson.forEach(item => {
                delete item.bindMacro
                delete item.isIcon
                delete item.bindCode
                delete item.bindType
            })

            // 恢复当前分页的按键数据（保持img、name等配置不变）
            this.restoreFnPageKeys(currentPage, 'restore')

            this.keyCodeJson.forEach(item => {
                delete item.bindMacro
                delete item.isIcon
                delete item.bindCode
                delete item.bindType
            })

            // reset 后同步清理宏绑定内存状态，避免监听再次回灌 bindMacro
            if (Array.isArray(macroStore.macros)) {
                macroStore.macros.forEach((macro) => {
                    macro.bindKeys = []
                    macro.bindingMode = null
                    macro.quickLaunch = null
                    macro.updatedAt = Date.now()
                })
            }

            // reset 后清理本地快捷启动缓存，避免刷新时读取旧值
            localStorage.setItem('layerQuickLaunchDump', JSON.stringify({ 0: [], 1: [], 2: [], 3: [] }))
            localStorage.setItem('layerQuickLaunchViewData', JSON.stringify({ 0: [], 1: [], 2: [], 3: [] }))

            const qKey = this.keyCodeJson.find(item => item?.code === 'KeyQ' || item?.name === 'Q')
            const wKey = this.keyCodeJson.find(item => item?.code === 'KeyW' || item?.name === 'W')
            console.log('[resetKeyCodeJson][QW-bind-check]', {
                Q: qKey ? {
                    keyCode: qKey.keyCode,
                    bindMacro: qKey.bindMacro,
                    isIcon: qKey.isIcon,
                    bindCode: qKey.bindCode,
                    bindType: qKey.bindType
                } : null,
                W: wKey ? {
                    keyCode: wKey.keyCode,
                    bindMacro: wKey.bindMacro,
                    isIcon: wKey.isIcon,
                    bindCode: wKey.bindCode,
                    bindType: wKey.bindType
                } : null
            })

            console.log(`[重置] 已重置键盘配置，保留第${currentPage}页的分页数据`)
        },
        // 设置当前选中的按索引
        setKeyboardIndex(index) {
            this.keyboardIndex = index
        },
        // 切换键盘语言
        setLanguage(language) {
            if (!this.supportedLanguages.includes(language)) {
                console.warn(`Invalid language: ${language}, keeping current language: ${this.currentLanguage}`)
                return
            }
            this.currentLanguage = language
            // 保存语言设置到 localStorage
            localStorage.setItem('keyboardLanguage', language)
            // 更新键盘数据以应用新的语言配置
            this.updateKeyboardData('更新键盘数据以应用新的语言配置')
        },
        // 获取当前语言
        getLanguage() {
            return this.currentLanguage
        },
        // 重置到默认语言（纯净的 keyboard.json）
        resetToDefaultLanguage() {
            this.currentLanguage = 'default'
            // 保存默认语言设置到 localStorage
            localStorage.setItem('keyboardLanguage', 'default')
            this.updateKeyboardData('已重置到默认语言配置')
            console.log('已重置到默认语言配置')
        },
        // 切换键盘布局
        setLayout(layout) {
            if (!this.supportedLayouts.includes(layout)) {
                console.warn(`Invalid layout: ${layout}, keeping current layout: ${this.currentLayout}`)
                return
            }
            this.currentLayout = layout
            // 更新键盘数据以应用新的布局配置
            this.updateKeyboardData('更新键盘数据以应用新的布局配置')
        },
        // 获取当前布局
        getLayout() {
            return this.currentLayout
        }
    }
})
